import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { defineMessages, FormattedMessage } from 'react-intl';
import { useCallback, useMemo, useReducer, useState } from 'react';
import { isLayerSwitcherGroupState } from '@tmapy/config';
import { useMessage } from '@tmapy/intl';
import { QUERY_STATE, unaccentAndLower } from '@tmapy/utils';
import { actionUpdateHashParams } from '@tmapy/router';
import { useSelector } from '@tmapy/redux';
import { DangerAlert, Form, Icon, Input, SecondaryBtn, SIZE, SvgPlus } from '@tmapy/style-guide';
import './LayerSwitcher.scss';
import { isLayerSwitcherGroupProps } from '../utils';
import { actionAddLayersByURL, actionLayerSetFilter } from '../actions';
import { LayerGroup } from './LayerGroup';
const msg = defineMessages({
    filterPlaceholder: {
        id: 'layer-switcher.filter.placeholder',
        defaultMessage: 'Filtrovat vrstvy a skupiny',
    },
    load: {
        id: 'layer-switcher.addLayer.load',
        defaultMessage: 'Načíst',
    },
    loadError: {
        id: 'layer-switcher.addLayer.loadError',
        defaultMessage: 'Vrstvy se nepovedlo načíst.',
    },
    loadInputPlaceholder: {
        id: 'layer-switcher.addLayer.input.placeholder',
        defaultMessage: 'Zadejte URL WMS služby...',
    },
});
const isFilterMatch = (filter, value) => unaccentAndLower(value).includes(filter);
export const LayerSwitcher = ({ layers, resolution, layerSwitcher, routes, interaction, dispatch, }) => {
    const formatMessage = useMessage();
    const layerSwitcherLayers = useMemo(() => {
        const addLayerLabel = (layer) => {
            if (isLayerSwitcherGroupState(layer)) {
                return {
                    // group
                    ...layer,
                    children: layer.children.map(addLayerLabel),
                    label: formatMessage.fallback([`layerSwitcher.group.${layer.groupId}`]) ??
                        layer.label ??
                        layer.groupId,
                    subtitle: formatMessage.fallback([`layerSwitcher.group.${layer.groupId}.subtitle`]),
                };
            }
            else {
                return {
                    ...layer,
                    label: formatMessage.fallback([`layer.${layer.layerId}`]) ?? layer.label ?? layer.layerId,
                };
            }
        };
        return layerSwitcher.layers.map(addLayerLabel);
    }, [formatMessage, layerSwitcher.layers]);
    const handleFilterChange = useCallback((event) => {
        if (!event.currentTarget.value) {
            dispatch(actionLayerSetFilter(null));
        }
        const filter = unaccentAndLower(event.currentTarget.value);
        const groupIds = new Set();
        const layerIds = new Set();
        const addChild = (props, parentGroups = []) => {
            if (isLayerSwitcherGroupProps(props)) {
                props.children.forEach((child) => addChild(child, [...parentGroups, props.groupId]));
                parentGroups.map((group) => groupIds.add(group));
                groupIds.add(props.groupId);
            }
            else {
                parentGroups.map((group) => groupIds.add(group));
                layerIds.add(props.layerId);
            }
        };
        const filterLayers = (props, parentGroups = []) => {
            if (isLayerSwitcherGroupProps(props)) {
                props.children.forEach((layer) => filterLayers(layer, [...parentGroups, props.groupId]));
                if (isFilterMatch(filter, props.label) ||
                    (props.subtitle && isFilterMatch(filter, props.subtitle))) {
                    addChild(props, parentGroups);
                }
            }
            else {
                if (isFilterMatch(filter, props.label)) {
                    parentGroups.map((group) => groupIds.add(group));
                    layerIds.add(props.layerId);
                }
            }
        };
        layerSwitcherLayers.forEach((layer) => filterLayers(layer, []));
        dispatch(actionLayerSetFilter({
            filter,
            groupIds: Array.from(groupIds),
            layerIds: Array.from(layerIds),
        }));
    }, [dispatch, layerSwitcherLayers]);
    const [isInputChanged, setInputChanged] = useState(false);
    const [addServiceUrl, setAddServiceUrl] = useState('');
    const [addServiceUrlState, setAddServiceUrlState] = useReducer((_, action) => {
        return action;
    }, { queryState: QUERY_STATE.EMPTY, error: null });
    const handleAddServiceUrlChange = useCallback((event) => {
        setAddServiceUrl(event.currentTarget.value);
        setInputChanged(true);
    }, []);
    const hashParams = useSelector((state) => state.router.hashParams);
    const handleSubmit = useCallback(async () => {
        const oldExtLayers = hashParams.extLayers ? hashParams.extLayers.split('*') : [];
        setAddServiceUrlState({ error: null, queryState: QUERY_STATE.WAITING });
        setInputChanged(false);
        try {
            const idPrefix = oldExtLayers.length;
            await dispatch(actionAddLayersByURL(addServiceUrl, `_${idPrefix}`));
            const extLayers = oldExtLayers?.length
                ? [...oldExtLayers, addServiceUrl].join('*')
                : addServiceUrl;
            dispatch(actionUpdateHashParams({
                extLayers,
            }));
            setAddServiceUrlState({ error: null, queryState: QUERY_STATE.SUCCESS });
            setAddServiceUrl('');
        }
        catch (err) {
            const error = err instanceof Error ? err : null;
            setAddServiceUrlState({ error, queryState: QUERY_STATE.FAIL });
        }
    }, [dispatch, addServiceUrl, hashParams.extLayers]);
    return (_jsxs(_Fragment, { children: [layerSwitcher.filter.isVisible && (_jsx("div", { className: 'lsw-layerSwitcher--filter sg-a-d-f sg-a-jc-s', children: _jsx(Input, { placeholder: formatMessage(msg.filterPlaceholder), value: layerSwitcher.filter.value ?? '', onChange: handleFilterChange }) })), _jsxs("div", { className: 'lsw-layerSwitcher--tree', children: [_jsx(LayerGroup, { layerSwitcher: layerSwitcherLayers, layers: layers, routes: routes, dispatch: dispatch, resolution: resolution, interaction: interaction, savedGroupVisibility: layerSwitcher.savedGroupVisibility }), layerSwitcher.enableCustomLayers && (_jsx(Form, { onSubmit: handleSubmit, children: _jsxs("div", { className: 'lsw-layerSwitcher--addLayer', children: [_jsx("div", { className: 'lsw-layerSwitcher--addIcon', children: _jsx(Icon, { element: _jsx(SvgPlus, {}), size: SIZE.SML }) }), _jsx("div", { className: 'lsw-layerSwitcher--addInput', children: _jsx(Input, { type: 'url', isRequired: true, value: addServiceUrl, placeholder: formatMessage(msg.loadInputPlaceholder), onChange: handleAddServiceUrlChange }) }), _jsx(SecondaryBtn, { type: 'submit', size: SIZE.SML, isDisabled: addServiceUrl === '', queryState: addServiceUrlState.queryState === QUERY_STATE.WAITING ? 'waiting' : undefined, children: _jsx(FormattedMessage, { ...msg.load }) }), addServiceUrlState.queryState === QUERY_STATE.FAIL && !isInputChanged && (_jsx("div", { className: 'lsw-layerSwitcher--addAlert', children: _jsx(DangerAlert, { children: _jsx(FormattedMessage, { ...msg.loadError }) }) }))] }) }))] })] }));
};
//# sourceMappingURL=LayerSwitcher.js.map